import { useLocation } from '@reach/router';
import { graphql } from 'gatsby';
import React, { useEffect, useMemo, useState } from 'react';
import { I18nextContext } from 'gatsby-plugin-react-i18next';

import { IconPointCircle } from '../components/Icons';

import Layout from '../components/Layout';
import Toc from '../components/Toc';
import SEO from '../components/seo';

import './tutorial.scss';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import Breadcrumbs from '../components/Breadcrumbs/Breadcrumbs';
import MiscellaneousTutorialsBoxs from '../components/MiscellaneousTutorialsBox/MiscellaneousTutorialsBoxs';
import {
  BACK,
  CLOUD_PAGE,
  CLOUD_PATH,
  EXAMPLES_PAGE,
  EXAMPLES_PATH,
  HARDWARE_PAGE,
  HARDWARE_PATH,
  LANGUAGE_REFERENCE_PAGE,
  LANGUAGE_REFERENCE_PATH,
  LEARN_PAGE,
  LEARN_PATH,
  MICROPYTHON_PAGE,
  MYCROPYTHON_PATH,
  PROGRAMMING_PAGE,
  RETIRED_BOARDS,
  RETIRED_KITS,
  RETIRED_PAGE,
  RETIRED_PATH,
  RETIRED_SHIELDS,
  SIDEBAR_CLOUD,
  SIDEBAR_EXAMPLES,
  SIDEBAR_HARDWARE,
  SIDEBAR_IDE_SOFTWARE,
  SIDEBAR_LANGUAGE,
  SIDEBAR_LEARN,
  SIDEBAR_LEGACY_DOCS,
  SIDEBAR_MYCROPYTHON,
  SIDEBAR_PLC_SOFTWARE,
  SIDEBAR_PROGRAMMING,
  SIDEBAR_SOFTWARE,
  SIDEBAR_TUTORIALS,
  SOFTWARE_IDE_PAGE,
  SOFTWARE_PAGE,
  SOFTWARE_PATH,
  SOFTWARE_PLC_PAGE,
  TUTORIALS_PAGE,
  TUTORIAL_COMMUNICATION,
  TUTORIAL_GENERIC,
  TUTORIAL_NANO_GENERIC,
  TUTORIAL_PROJECT,
  TUTORIAL_SECURITY,
} from '../utils/constants';
import {
  formatString,
  getActualPage,
  getLanguageReferenceSideBarDataRefactored,
  getMicropythonSideBarDataRefactored,
  getRetiredSideBarDataRefactored,
  getSoftwareSideBarDataRefactored,
  getTutorialHardwareSideBarDataRefactored,
  getTutorialSideBarDataRefactored,
} from '../utils/functions';
import useStaticWindowSize from '../hooks/useStaticWindowSize';
import TutorialSideBar from '../components/SideBar/TutorialSidebar';
import { Modal } from '../components/Modal/Modal';
import { TutorialProps } from '../entities/Tutorials';
import Feedback from '../components/Feedback/Feedback';
import InfoBanner from '../components/InfoBanner/InfoBanner';

export default function Tutorial({ data }: TutorialProps): React.ReactElement {
  const { frontmatter, body, tableOfContents } = data.file.childMdx;
  const context = React.useContext(I18nextContext);

  const location = useLocation();
  const parts = location.pathname.split('/');

  const { width } = useStaticWindowSize();

  const [imgModalOpened, setImgModalOpened] = useState(false);
  const [imgModal, setImgModal] = useState(null);
  const [isExtra, setIsExtra] = useState(false);

  const [domLoaded, setDomLoaded] = useState(false);

  useEffect(() => {
    setDomLoaded(true);
  }, []);

  //Click an img open a modal that show it, instead of change browser tab.
  if (typeof window !== 'undefined' && !imgModalOpened) {
    window.addEventListener('click', (e) => {
      if (
        (e.target as any).nodeName === 'IMG' &&
        (e.target as any).getAttribute('class') !== 'tutorial__modal-img' &&
        (e.target as any).getAttribute('src').slice(-4) !== '.gif' &&
        (e.target as any).getAttribute('src').slice(-4) !== '.svg'
      ) {
        setImgModal(e.target);
        setImgModalOpened(true);
      } else if (
        (e.target as any).nodeName === 'IMG' &&
        ((e.target as any).getAttribute('src').slice(-4) === '.gif' || (e.target as any).getAttribute('src').slice(-4) === '.svg')
      ) {
        setImgModal(e.target);
        setImgModalOpened(true);
        setIsExtra(true);
      }
    });
  }

  //Change Contribute Button's URL in case of symlinked tutorials
  let contributeBoxUrl = data.file.relativePath;

  if (data.file.childMdx.frontmatter.hardware && data.parent && data.file.childMdx.frontmatter.hardware[0][0] !== '_') {
    let relativeDirDifference = data.file.relativePath.length - data.parent.relativeDirectory.length;
    let relativeTutorial = data.file.relativePath.slice(-relativeDirDifference);
    let originalTutorialDir = data.file.childMdx.frontmatter.hardware[0] + relativeTutorial;
    contributeBoxUrl = originalTutorialDir;
    if (data.file.childMdx.frontmatter.contributeURL) contributeBoxUrl = data.file.childMdx.frontmatter.contributeURL + relativeTutorial;
  }

  const isDesktop = useMemo(() => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth > 768) return true;
      return false;
    } else return true;
  }, []);

  const actualPage = getActualPage(location.pathname.split('/'));

  const sideBarData = useMemo(() => {
    if (actualPage.title === SIDEBAR_CLOUD) return getTutorialSideBarDataRefactored(data.pages);
    else if (actualPage.title === SIDEBAR_HARDWARE) return getTutorialHardwareSideBarDataRefactored(data.parent?.childMdx.frontmatter.title, data);
    else if (actualPage.title === SIDEBAR_MYCROPYTHON) return getMicropythonSideBarDataRefactored(data, data.images);
    else if (actualPage.title === SIDEBAR_SOFTWARE || actualPage.title === SIDEBAR_IDE_SOFTWARE || actualPage.title === SIDEBAR_PLC_SOFTWARE)
      return getSoftwareSideBarDataRefactored(data);
    else if (actualPage.title === SIDEBAR_LEARN) return getTutorialSideBarDataRefactored(data.learnPages);
    else if (actualPage.title === SIDEBAR_EXAMPLES) return getTutorialSideBarDataRefactored(data.examplesPages);
    else if (actualPage.title === SIDEBAR_LEGACY_DOCS) return getRetiredSideBarDataRefactored(data);
    else if (actualPage.title === SIDEBAR_LANGUAGE) return getLanguageReferenceSideBarDataRefactored(data, parts[2]);
    else return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualPage.title, context.language, data]);
  // Manage main title of sidebar
  const sideBarTitle = useMemo(() => {
    if (parts?.length >= 1 && !parts[1]) return 'Hardware';
    else if (parts?.length >= 1 && parts[1] === SOFTWARE_PATH) return 'Software tools';
    else if (parts?.length >= 1 && parts[1] === MYCROPYTHON_PATH) return SIDEBAR_MYCROPYTHON;
    else if (parts?.length >= 1 && parts[1] === CLOUD_PATH) return SIDEBAR_CLOUD;
    else if (parts?.length >= 1 && parts[1] === EXAMPLES_PATH) return SIDEBAR_EXAMPLES;
    else if (parts?.length >= 1 && parts[1] === LEARN_PATH) return SIDEBAR_LEARN;
    else if (parts?.length >= 1 && parts[1] === RETIRED_PATH) return SIDEBAR_LEGACY_DOCS;
    else if (
      (parts?.length >= 1 && parts[1] === LANGUAGE_REFERENCE_PATH && (parts[2] === 'en' || parts[2] === 'de' || parts[2] === 'pt')) ||
      (parts?.length >= 1 && parts[1] === 'en') ||
      (parts?.length >= 1 && parts[1] === 'de') ||
      (parts?.length >= 1 && parts[1] === 'pt')
    )
      return SIDEBAR_LANGUAGE;
    else if (
      parts?.length >= 1 &&
      parts[1] === HARDWARE_PATH &&
      (parts[2] === TUTORIAL_COMMUNICATION ||
        parts[2] === TUTORIAL_GENERIC ||
        parts[2] === TUTORIAL_PROJECT ||
        parts[2] === TUTORIAL_NANO_GENERIC ||
        parts[2] === TUTORIAL_SECURITY)
    )
      return SIDEBAR_TUTORIALS;
    else return 'Hardware';
  }, [parts]);

  const buildBreadcrumbs = useMemo(() => {
    if (actualPage.title === SIDEBAR_SOFTWARE) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_SOFTWARE, url: SOFTWARE_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_IDE_SOFTWARE) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_SOFTWARE, url: SOFTWARE_PAGE },
        { title: SIDEBAR_IDE_SOFTWARE, url: SOFTWARE_IDE_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_PLC_SOFTWARE) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_SOFTWARE, url: SOFTWARE_PAGE },
        { title: SIDEBAR_PLC_SOFTWARE, url: SOFTWARE_PLC_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_MYCROPYTHON) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_PROGRAMMING, url: PROGRAMMING_PAGE },
        { title: SIDEBAR_MYCROPYTHON, url: MICROPYTHON_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_LEARN) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_LEARN, url: LEARN_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_EXAMPLES) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_PROGRAMMING, url: PROGRAMMING_PAGE },
        { title: SIDEBAR_EXAMPLES, url: EXAMPLES_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_CLOUD) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_CLOUD, url: CLOUD_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_TUTORIALS) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_TUTORIALS, url: TUTORIALS_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_LEGACY_DOCS) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_HARDWARE, url: HARDWARE_PAGE },
        { title: SIDEBAR_LEGACY_DOCS, url: RETIRED_PAGE },
        { title: frontmatter.title, url: null },
      ];
      // Language Reference
    } else if (actualPage.title === SIDEBAR_LANGUAGE) {
      const base_breadcrumbs = [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_PROGRAMMING, url: PROGRAMMING_PAGE },
        {
          title: SIDEBAR_LANGUAGE,
          url: LANGUAGE_REFERENCE_PAGE,
        },
        {
          title: parts?.length >= 1 ? decodeURIComponent(parts[3].charAt(0).toUpperCase() + parts[3].slice(1)) : '',
          url: LANGUAGE_REFERENCE_PAGE + '#' + parts[3],
        },
      ];

      if (parts.length < 7) {
        return [...base_breadcrumbs, { title: frontmatter.title, url: null }];
      } else if (parts.length < 8) {
        return [...base_breadcrumbs, { title: frontmatter.title, url: null }];
      } else if (parts.length <= 9) {
        return [
          ...base_breadcrumbs,
          { title: formatString(parts[5]), url: `/${parts.slice(1, 6).join('/')}` },
          { title: frontmatter.title, url: null },
        ];
      } else return base_breadcrumbs;
    } else {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_HARDWARE, url: HARDWARE_PAGE },
        { title: data.parent?.childMdx.frontmatter.title ?? SIDEBAR_HARDWARE, url: data.parent?.fields.slug ?? HARDWARE_PAGE },
        { title: frontmatter.title, url: null },
      ];
    }
  }, [actualPage.title, data.parent?.childMdx.frontmatter.title, data.parent?.fields.slug, frontmatter.title, parts]);

  return (
    <>
      {domLoaded && (
        <Layout>
          <SEO title={frontmatter.title} description={frontmatter.description} />
          <main className="page-container">
            <TutorialSideBar
              links={[]}
              nestedCategories={sideBarData}
              selectedItem={data.parent?.childMdx.frontmatter.title}
              actualPage={sideBarTitle}
              leafPage={frontmatter.title}
              prevPage={{ title: BACK, pathname: actualPage.url }}
              isTutorials={width < 1024}
              localization={true}
            />
            <div className="page-layout tutorial">
              <div className="tutorial__container">
                <div className="tutorial__content">
                  <Breadcrumbs actualPage={frontmatter.title} list={buildBreadcrumbs} />
                  {parts?.length >= 1 &&
                    parts[1] === RETIRED_PATH &&
                    (parts[2] === RETIRED_BOARDS || parts[2] === RETIRED_SHIELDS || parts[2] === RETIRED_KITS) && (
                      <InfoBanner title={'Note: this page refers to a product that is retired.'} />
                    )}
                  {['end-of-life', 'retired'].includes(data.parent?.childMdx.frontmatter?.status) && (
                    <InfoBanner title={'This tutorial refers to a product that has reached its end-of-life status.'} />
                  )}
                  <h1>{frontmatter.title}</h1>
                  <p>{frontmatter.description}</p>
                  <div className="tutorial__metadata">
                    {frontmatter.author && (
                      <div className="tutorial__metadata--author">
                        Author <IconPointCircle /> {frontmatter.author}
                      </div>
                    )}
                    {data.file.fields?.gitLogLatestDate && (
                      <div>
                        Last revision <IconPointCircle />
                        {new Date(data.file.fields?.gitLogLatestDate).toLocaleString([], {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                        })}
                      </div>
                    )}
                  </div>
                  <div className="tutorial__text">
                    <MDXRenderer>{body}</MDXRenderer>
                  </div>
                  <MiscellaneousTutorialsBoxs contributeBoxUrl={contributeBoxUrl} />
                </div>
                {isDesktop && (
                  <div className="tutorial__toc">
                    <Toc toc={tableOfContents}></Toc>
                  </div>
                )}
              </div>
              {/* <TutorialSupplies
            hardwareSupplies={frontmatter.hardware}
            softwareSupplies={frontmatter.software}
            librariesSupplies={frontmatter.libraries}
          /> */}
              <Feedback
                params={{
                  type: 'tutorial',
                  title: frontmatter.title,
                  slug: data.file.relativePath,
                }}
              />
            </div>
            <Modal
              full={true}
              isOpen={imgModalOpened}
              onClose={() => {
                setImgModalOpened(false);
                setImgModal(null);
                setIsExtra(false);
              }}
              isDismissable={true}
              className="tutorial__modal-container"
            >
              {isExtra ? (
                <img className="tutorial__modal-img" src={'https://docs.arduino.cc' + imgModal?.getAttribute('src')} alt="img" />
              ) : (
                <img
                  className="tutorial__modal-img"
                  src={
                    'https://docs.arduino.cc' +
                    imgModal?.getAttribute('srcset').split(',')[imgModal?.getAttribute('srcset').split(',').length - 1].split(' ')[0]
                  }
                  alt="img"
                />
              )}
            </Modal>
          </main>
        </Layout>
      )}
    </>
  );
}

export const pageQuery = graphql`
  query ($slug: String!, $parent: String!, $product: String!, $language: String!) {
    locales: allLocale(filter: { ns: { in: ["index"] }, language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    file: file(fields: { slug: { eq: $slug } }) {
      fields {
        gitLogLatestDate
      }
      relativePath
      childMdx {
        body
        internal {
          contentFilePath
        }
        tableOfContents
        frontmatter {
          title
          description
          author
          libraries {
            name
            url
          }
          software
          hardware
          contributeURL
        }
      }
    }
    parent: file(fields: { slug: { eq: $parent } }) {
      fields {
        slug
      }
      relativeDirectory
      childMdx {
        frontmatter {
          title
          url_shop
          status
        }
      }
    }
    families: allFile(filter: { base: { eq: "family.md" } }, sort: { fields: { order: ASC } }) {
      edges {
        node {
          relativeDirectory
          childMdx {
            frontmatter {
              title
              description
              businessUnit
              anchor
            }
          }
        }
      }
    }
    categories: allFile(filter: { base: { eq: "category.md" } }) {
      edges {
        node {
          relativeDirectory
          childMdx {
            frontmatter {
              title
            }
          }
        }
      }
    }
    products: allFile(filter: { base: { eq: "product.md" } }) {
      edges {
        node {
          relativeDirectory
          fields {
            slug
            category
          }
          childMdx {
            frontmatter {
              title
              core
            }
          }
        }
      }
    }
    pages: allFile(filter: { fields: { type: { eq: "arduino-cloud" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
            internal {
              contentFilePath
            }
          }
        }
      }
    }
    retiredPages: allFile(filter: { fields: { type: { eq: "retired" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              icon
            }
          }
        }
      }
    }
    micropythonPages: allFile(filter: { fields: { type: { eq: "micropython" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
    softwarePages: allFile(filter: { fields: { type: { eq: "software" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
    softwareSubpages: allFile(filter: { fields: { type: { eq: "softwarepage" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
    learnPages: allFile(filter: { fields: { type: { eq: "learn" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
    examplesPages: allFile(filter: { fields: { type: { eq: "built-in-examples" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
    tutorials: allFile(filter: { fields: { product: { eq: $product } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          fields {
            slug
            gitLogLatestDate
          }
          childMdx {
            frontmatter {
              title
              description
              tags
              author
              difficulty
            }
          }
        }
      }
    }
    images: allFile(filter: {extension: {regex: "/(jpg)|(jpeg)|(png)/"}, base: {regex: "/\\py-hero-banner.(jpe?g|png|gif|bmp|svg)$/i"}}) {
      edges {
        node {
          id
          relativeDirectory
          childImageSharp {
            gatsbyImageData(quality: 100, placeholder: BLURRED)
          }
        }
      }
    }
    en: allFile(filter: { fields: { type: { eq: "en" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
            gitLogLatestDate
          }
          childMdx {
            body
            frontmatter {
              title
              categories
              description
              subCategories
              leaf
            }
          }
        }
      }
    }
    de: allFile(filter: { fields: { type: { eq: "de" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
            gitLogLatestDate
          }
          childMdx {
            body
            frontmatter {
              title
              categories
              description
              subCategories
              leaf
            }
          }
        }
      }
    }
    pt: allFile(filter: { fields: { type: { eq: "pt" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
            gitLogLatestDate
          }
          childMdx {
            body
            frontmatter {
              title
              categories
              subCategories
              leaf
            }
          }
        }
      }
    }
  }
`;
